import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserMultiFormatReader, type IScannerControls } from "@zxing/browser";
import { Camera, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogBody, DialogHeader, DialogTitle } from "./ui/dialog";

/**
 * Barcode skanerlash tugmasi (kamera ikonkasi).
 * Bosilganda kamera ochiladi; barcode topilsa `onScan` chaqiriladi va kamera yopiladi.
 * Faqat HTTPS'da ishlaydi (brauzer kamera ruxsati).
 */
export function BarcodeScannerButton({
  onScan,
  disabled,
}: {
  onScan: (code: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("ombor");
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="icon"
        title={t("scanner.buttonTitle")}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <Camera />
      </Button>
      {open && (
        <ScannerDialog
          onClose={() => setOpen(false)}
          onScan={(code) => {
            setOpen(false);
            onScan(code);
          }}
        />
      )}
    </>
  );
}

function ScannerDialog({
  onClose,
  onScan,
}: {
  onClose: () => void;
  onScan: (code: string) => void;
}) {
  const { t } = useTranslation("ombor");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();

    (async () => {
      try {
        const controls = await reader.decodeFromVideoDevice(
          undefined,
          videoRef.current ?? undefined,
          (result, _e, ctrl) => {
            if (result && !cancelled) {
              ctrl.stop();
              onScan(result.getText());
            }
          },
        );
        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
      } catch (e) {
        const name = (e as { name?: string }).name;
        if (name === "NotAllowedError" || name === "NotFoundError") {
          setErr(t("scanner.permission"));
        } else {
          setErr(t("scanner.openFailed"));
        }
      }
    })();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
    };
  }, [onScan, t]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogHeader>
        <DialogTitle>{t("scanner.dialogTitle")}</DialogTitle>
      </DialogHeader>
      <DialogBody className="space-y-3">
        {err ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive px-3 py-4 text-sm text-center">
            {err}
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-md bg-black aspect-video">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              muted
              playsInline
            />
            <div className="pointer-events-none absolute inset-x-6 top-1/2 -translate-y-1/2 h-0.5 bg-primary/70" />
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={onClose}
        >
          <X />
          {t("scanner.close")}
        </Button>
      </DialogBody>
    </Dialog>
  );
}
